import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Pagination,
  Tab,
  Tabs
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

interface ContactRow {
  _id: Id<'contacts'>;
  name: string;
  email: string;
  phone?: string;
  status: 'pending' | 'purchased' | 'not_purchased';
  amount?: number;
  isPaid: boolean | null;
  createdAt: number;
  consultorId?: string;
  collaboratorId?: string;
  associatedUserName?: string | null;
  url: string;
  urlOwnerName?: string | null;
}

export const ClientesPage = () => {
  const { userData } = useAuth();
  const rawContacts = useQuery(api.contacts.listContacts) ?? [];
  const contacts = rawContacts as ContactRow[];

  const updateStatus = useMutation(api.contacts.updateContactStatus);
  const updateAmount = useMutation(api.contacts.updateContactAmount);
  const updateIsPaid = useMutation(api.contacts.updateContactIsPaid);
  const deleteContact = useMutation(api.contacts.deleteContact);

  const [filteredContacts, setFilteredContacts] = useState<ContactRow[]>([]);
  const [editingContact, setEditingContact] = useState<{ id: Id<'contacts'>; amount?: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const contactsPerPage = 10;

  // Filtrar contactos
  useEffect(() => {
    let result = [...contacts];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(contact =>
        contact.name.toLowerCase().includes(term) ||
        contact.email.toLowerCase().includes(term) ||
        (contact.phone?.toLowerCase().includes(term) || false) ||
        (contact.associatedUserName?.toLowerCase().includes(term) || false) ||
        (contact.urlOwnerName?.toLowerCase().includes(term) || false)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(contact => contact.status === statusFilter);
    }

    setFilteredContacts(result);
    setCurrentPage(1);
  }, [contacts, searchTerm, statusFilter]);

  // Paginación
  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = filteredContacts.slice(indexOfFirstContact, indexOfLastContact);
  const pageCount = Math.ceil(filteredContacts.length / contactsPerPage);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStatusChange = async (contactId: Id<'contacts'>, newStatus: string) => {
    if (userData?.role !== 'admin' && userData?.role !== 'consultor') return;
    try {
      await updateStatus({ id: contactId, status: newStatus as 'pending' | 'purchased' | 'not_purchased' });
    } catch (error) {
      console.error('Error al actualizar el estado:', error);
    }
  };

  const handleAmountChange = (contactId: Id<'contacts'>, amount: string) => {
    if (userData?.role !== 'admin' && userData?.role !== 'consultor') return;
    setEditingContact({ id: contactId, amount: parseFloat(amount) || undefined });
  };

  const saveAmount = async () => {
    if (userData?.role !== 'admin' && userData?.role !== 'consultor') return;
    if (!editingContact) return;
    try {
      await updateAmount({ id: editingContact.id, amount: editingContact.amount });
      setEditingContact(null);
    } catch (error) {
      console.error('Error al guardar el importe:', error);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, color: '#1a237e' }}>Clientes</Typography>
        <Typography variant="body1" color="text.secondary">Gestión de clientes y contactos</Typography>
      </Box>

      <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#1a237e' }}>
              {userData?.role === 'admin' ? 'Todos los Clientes' : 'Mis Clientes'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="status-filter-label">Estado</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="pending">Pendientes</MenuItem>
                  <MenuItem value="purchased">Comprados</MenuItem>
                  <MenuItem value="not_purchased">No han comprado</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ width: { xs: '100%', sm: 240 } }}>
                <InputLabel htmlFor="search-contacts">Buscar</InputLabel>
                <OutlinedInput
                  id="search-contacts"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  endAdornment={<InputAdornment position="end"><SearchIcon /></InputAdornment>}
                  label="Buscar"
                  placeholder="Nombre, email, teléfono..."
                />
              </FormControl>
            </Box>
          </Box>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              mb: 2,
              '& .MuiTabs-indicator': { backgroundColor: '#1a237e' },
              '& .Mui-selected': { color: '#1a237e', fontWeight: 'bold' },
            }}
          >
            <Tab label="Todos" onClick={() => setStatusFilter('all')} />
            <Tab label="Pendientes" onClick={() => setStatusFilter('pending')} />
            <Tab label="Han comprado" onClick={() => setStatusFilter('purchased')} />
            <Tab label="No han comprado" onClick={() => setStatusFilter('not_purchased')} />
          </Tabs>

          <TableContainer sx={{ maxHeight: 440, overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Teléfono</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Comisión</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Pagado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>URL Propietario</TableCell>
                  {userData?.role === 'admin' && (
                    <>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Asociado a</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Acciones</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentContacts.length > 0 ? (
                  currentContacts.map((contact) => (
                    <TableRow
                      key={contact._id}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{contact.name}</TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.phone}</TableCell>
                      <TableCell>
                        {(userData?.role === 'admin' || userData?.role === 'consultor') ? (
                          <Select
                            value={contact.status}
                            onChange={(e) => handleStatusChange(contact._id, e.target.value)}
                            size="small"
                            sx={{
                              '& .MuiSelect-select': {
                                py: 0.5,
                                bgcolor: contact.status === 'purchased' ? 'rgba(76, 175, 80, 0.1)' :
                                        contact.status === 'pending' ? 'rgba(255, 152, 0, 0.1)' :
                                        'rgba(244, 67, 54, 0.1)',
                                color: contact.status === 'purchased' ? 'success.main' :
                                      contact.status === 'pending' ? 'warning.main' : 'error.main',
                                fontWeight: 500,
                                borderRadius: 1
                              }
                            }}
                          >
                            <MenuItem value="pending">Pendiente</MenuItem>
                            <MenuItem value="purchased">Han comprado</MenuItem>
                            <MenuItem value="not_purchased">No han comprado</MenuItem>
                          </Select>
                        ) : (
                          <Box sx={{
                            py: 0.5, px: 1.5, display: 'inline-block',
                            bgcolor: contact.status === 'purchased' ? 'rgba(76, 175, 80, 0.1)' :
                                    contact.status === 'pending' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            color: contact.status === 'purchased' ? 'success.main' :
                                  contact.status === 'pending' ? 'warning.main' : 'error.main',
                            fontWeight: 500, borderRadius: 1
                          }}>
                            {contact.status === 'purchased' ? 'Han comprado' :
                             contact.status === 'pending' ? 'Pendiente' : 'No han comprado'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {(userData?.role === 'admin' || userData?.role === 'consultor') ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              type="number"
                              size="small"
                              value={editingContact?.id === contact._id && editingContact.amount !== undefined
                                ? editingContact.amount
                                : contact.amount || ''}
                              onChange={(e) => handleAmountChange(contact._id, e.target.value)}
                              disabled={contact.status !== 'purchased' || contact.isPaid === null}
                              inputProps={{ min: 0, step: '0.01' }}
                              sx={{ width: '100px' }}
                            />
                            {editingContact?.id === contact._id && contact.isPaid !== null && (
                              <Tooltip title="Guardar importe">
                                <IconButton onClick={saveAmount} size="small" color="primary">
                                  <SaveIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2">{contact.amount ? `${contact.amount}` : '-'}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(userData?.role === 'admin' || userData?.role === 'consultor') && (
                          <Select
                            value={contact.isPaid === null ? 'no_commission' : (contact.isPaid ? 'paid' : 'unpaid')}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === 'no_commission') {
                                updateIsPaid({ id: contact._id, isPaid: null, amount: 0 });
                              } else {
                                updateIsPaid({ id: contact._id, isPaid: value === 'paid' });
                              }
                            }}
                            size="small"
                          >
                            <MenuItem value="paid">Sí</MenuItem>
                            <MenuItem value="unpaid">No</MenuItem>
                            <MenuItem value="no_commission">Sin Comisión</MenuItem>
                          </Select>
                        )}
                        {userData?.role === 'colaborador' && (
                          contact.isPaid === null ? 'Sin Comisión' : (contact.isPaid ? 'Sí' : 'No')
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.createdAt ? new Date(contact.createdAt).toLocaleString('es-ES') : '-'}
                      </TableCell>
                      <TableCell>{contact.urlOwnerName || 'N/A'}</TableCell>
                      {userData?.role === 'admin' && (
                        <>
                          <TableCell>{contact.associatedUserName || 'N/A'}</TableCell>
                          <TableCell>
                            <Tooltip title="Eliminar contacto">
                              <IconButton
                                onClick={async () => {
                                  if (window.confirm(`¿Estás seguro de que deseas eliminar el contacto ${contact.name}?`)) {
                                    try {
                                      await deleteContact({ id: contact._id });
                                    } catch (error) {
                                      console.error('Error al eliminar contacto:', error);
                                    }
                                  }
                                }}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={userData?.role === 'admin' ? 10 : 8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">No se encontraron contactos</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredContacts.length > contactsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
